/**
 * Schedule Parser Utilities
 *
 * Functions for parsing time strings and checking if current time
 * falls within a schedule's time range.
 */

import { ParsedTime, Schedule } from '../types';

/**
 * Parses a time string in "HH:MM" format to hours and minutes.
 *
 * @param timeStr - Time string in 24h format (e.g., "08:00", "18:30")
 * @returns ParsedTime object with hours and minutes
 * @throws Error if format is invalid
 */
export function parseTime(timeStr: string): ParsedTime {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);

  if (!match) {
    throw new Error(`Invalid time format: "${timeStr}". Expected "HH:MM".`);
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours < 0 || hours > 23) {
    throw new Error(`Invalid hours: ${hours}. Must be between 0 and 23.`);
  }

  if (minutes < 0 || minutes > 59) {
    throw new Error(`Invalid minutes: ${minutes}. Must be between 0 and 59.`);
  }

  return { hours, minutes };
}

/**
 * Converts a ParsedTime to minutes since midnight.
 * Useful for time comparisons.
 */
export function timeToMinutes(time: ParsedTime): number {
  return time.hours * 60 + time.minutes;
}

/**
 * Chromium/Tizen WebKit: {@code hour12:false} + {@code hour:'numeric'} pode
 * emitir "24" entre 00:00–00:59. Isso faz {@link isTimeInRange} falhar contra
 * endTime "23:59" (1451min > 1439min) → campanha all-day fica inactive à
 * meia-noite (task force Renato 2026-06-01, log TIZEN-YPTNTPMDJTIXP).
 */
function normalizeIntlHour(raw: string | undefined): number {
  let hours = parseInt(raw || '0', 10);
  if (hours === 24) hours = 0;
  return hours;
}

/**
 * Memoized {@link Intl.DateTimeFormat} instances, keyed by timezone.
 *
 * Constructing an {@code Intl.DateTimeFormat} is expensive on the Tizen JSC
 * engine — the FIRST cold construction pays ICU/tz-database warmup (~50-90×
 * the warm cost). The temporal filters call {@link getCurrentTimeInTimezone}
 * once per library item across up to 6 schedule layers, so a full "cold" pass
 * over the library (~684 items) used to build ~1.700 formatters and froze the
 * main thread for ~28s on the first sync after boot. Reusing a single
 * formatter per timezone (the timezone is effectively constant —
 * America/Sao_Paulo) drops that to one construction and also cuts the warm
 * per-tick cost from ~300ms to tens of ms. {@code formatToParts} is stateless,
 * so behaviour is identical.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getTimezoneFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timezone);
  if (!formatter) {
    // Throws RangeError for invalid timezones — caught by the caller's
    // try/catch, which falls back to local time (unchanged behaviour).
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      weekday: 'short',
    });
    formatterCache.set(timezone, formatter);
  }
  return formatter;
}

/**
 * Warms the formatter cache for the given timezone off the critical path
 * (e.g. at boot), so the one-time ICU load doesn't land inside the first
 * sync's schedule evaluation. Safe to call multiple times.
 */
export function prewarmScheduleFormatter(timezone = 'America/Sao_Paulo'): void {
  try {
    getTimezoneFormatter(timezone);
  } catch {
    /* invalid timezone — nothing to warm */
  }
}

/**
 * Gets the current time in a specific timezone.
 *
 * @param timezone - IANA timezone string (e.g., "America/Sao_Paulo")
 * @param now - Instant to evaluate (defaults to current time)
 * @returns Object with current hours, minutes, and day of week in that timezone
 */
export function getCurrentTimeInTimezone(
  timezone: string,
  now: Date = new Date(),
): {
  hours: number;
  minutes: number;
  dayOfWeek: number;
  date: Date;
} {
  try {
    const formatter = getTimezoneFormatter(timezone);

    const parts = formatter.formatToParts(now);
    const hourPart = parts.find((p) => p.type === 'hour');
    const minutePart = parts.find((p) => p.type === 'minute');
    const weekdayPart = parts.find((p) => p.type === 'weekday');

    const hours = normalizeIntlHour(hourPart?.value);
    const minutes = parseInt(minutePart?.value || '0', 10);

    // Convert weekday abbreviation to number (0-6)
    const weekdayMap: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    const dayOfWeek = weekdayMap[weekdayPart?.value || 'Sun'] ?? now.getDay();

    return { hours, minutes, dayOfWeek, date: now };
  } catch {
    // Fallback to local time if timezone is invalid
    console.warn(`Invalid timezone "${timezone}", falling back to local time`);
    return {
      hours: now.getHours(),
      minutes: now.getMinutes(),
      dayOfWeek: now.getDay(),
      date: now,
    };
  }
}

/**
 * Checks if the current time falls within a time range.
 * Handles overnight ranges (e.g., 22:00-06:00).
 *
 * @param currentTime - Current time as ParsedTime
 * @param startTime - Start time string (e.g., "08:00")
 * @param endTime - End time string (e.g., "18:00")
 * @returns true if current time is within range
 */
export function isTimeInRange(
  currentTime: ParsedTime,
  startTime: string,
  endTime: string,
): boolean {
  const start = parseTime(startTime);
  const end = parseTime(endTime);

  // Tizen/Chromium pode produzir hour=24 via Intl na madrugada
  const hours = currentTime.hours === 24 ? 0 : currentTime.hours;
  const currentMinutes = timeToMinutes({ hours, minutes: currentTime.minutes });
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);

  // Handle overnight range (e.g., 22:00 - 06:00)
  if (startMinutes > endMinutes) {
    // Current time is after start OR before end
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
  }

  // Normal range (e.g., 08:00 - 18:00)
  // End minute is inclusive — matches filterPlaylistBySchedule() in scheduler.store.ts
  // which uses string comparison (currentTime > endAt). Without <= here, endTime
  // "23:59" excludes the entire 23:59 minute and causes "Aguardando conteúdo".
  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
}

/**
 * Checks if a day of week is included in the schedule's days.
 *
 * @param dayOfWeek - Current day (0=Sun, 1=Mon, ..., 6=Sat)
 * @param daysOfWeek - Array of allowed days
 * @returns true if day is included
 */
export function isDayActive(dayOfWeek: number, daysOfWeek: number[]): boolean {
  if (!daysOfWeek || daysOfWeek.length === 0) return true;
  return daysOfWeek.some((d) => Number(d) === dayOfWeek);
}

/**
 * Checks if a schedule is currently active based on time and day.
 *
 * @param schedule - Schedule to check
 * @param now - Optional Date to check against (defaults to current time)
 * @returns true if schedule is active right now
 */
export function isScheduleActive(
  schedule: Schedule,
  now: Date = new Date(),
): boolean {
  const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
    schedule.timezone,
    now,
  );

  // Check day of week
  if (!isDayActive(dayOfWeek, schedule.daysOfWeek)) {
    return false;
  }

  // Check time range
  return isTimeInRange(
    { hours, minutes },
    schedule.startTime,
    schedule.endTime,
  );
}

/**
 * Finds the next time a schedule will become active.
 * Useful for calculating when to re-evaluate.
 *
 * @param schedule - Schedule to check
 * @returns Date when schedule will next be active, or null if never
 */
export function getNextScheduleActivation(
  schedule: Schedule,
  now: Date = new Date(),
): Date | null {
  const { hours, minutes, dayOfWeek, date } = getCurrentTimeInTimezone(
    schedule.timezone,
    now,
  );

  // If schedule has no valid days, return null
  if (schedule.daysOfWeek.length === 0) {
    return null;
  }

  const startTime = parseTime(schedule.startTime);
  const currentMinutes = timeToMinutes({ hours, minutes });
  const startMinutes = timeToMinutes(startTime);

  // Check if we can activate today
  if (
    schedule.daysOfWeek.includes(dayOfWeek) &&
    currentMinutes < startMinutes
  ) {
    const result = new Date(date);
    result.setHours(startTime.hours, startTime.minutes, 0, 0);
    return result;
  }

  // Find next valid day
  for (let i = 1; i <= 7; i++) {
    const nextDay = (dayOfWeek + i) % 7;
    if (schedule.daysOfWeek.includes(nextDay)) {
      const result = new Date(date);
      result.setDate(result.getDate() + i);
      result.setHours(startTime.hours, startTime.minutes, 0, 0);
      return result;
    }
  }

  return null;
}
